/**
 * EnhancedTabsEditor
 *
 * Property editor for enhanced-tabs control type
 * Configures tab appearance, colors, sizing, and behavior
 *
 * SRP: Responsible ONLY for configuring enhanced-tabs properties
 * Control Type: 'enhanced-tabs'
 *
 * Properties Configured (9):
 * 1. activeTabColor - Active tab text/indicator color
 * 2. inactiveTabColor - Inactive tab text color
 * 3. tabBackgroundColor - Tab background color
 * 4. indicatorColor - Active tab indicator color
 * 5. tabHeight - Height of tab header (px)
 * 6. orientation - Tabs position (horizontal/vertical)
 * 7. variant - Style variant (line/pills/bordered)
 * 8. animateIndicator - Animate indicator movement
 * 9. scrollableTabs - Enable horizontal scroll if needed
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';
import { SelectPropertyEditor } from '../BaseEditors/SelectPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';

/**
 * Enhanced tabs configuration
 */
interface EnhancedTabsConfig {
  activeTabColor?: string;
  inactiveTabColor?: string;
  tabBackgroundColor?: string;
  indicatorColor?: string;
  tabHeight?: number;
  orientation?: 'horizontal' | 'vertical';
  variant?: 'line' | 'pills' | 'bordered';
  animateIndicator?: boolean;
  scrollableTabs?: boolean;
}

/**
 * EnhancedTabsEditor Component
 * Renders property editors for all enhanced-tabs configuration
 */
const EnhancedTabsEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as EnhancedTabsConfig) || {},
    [control.config],
  );

  const handleActiveTabColorChange = useCallback(
    (value: string) => onConfigChange('activeTabColor', value),
    [onConfigChange],
  );

  const handleInactiveTabColorChange = useCallback(
    (value: string) => onConfigChange('inactiveTabColor', value),
    [onConfigChange],
  );

  const handleTabBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('tabBackgroundColor', value),
    [onConfigChange],
  );

  const handleIndicatorColorChange = useCallback(
    (value: string) => onConfigChange('indicatorColor', value),
    [onConfigChange],
  );

  const handleTabHeightChange = useCallback(
    (value: number) => onConfigChange('tabHeight', value),
    [onConfigChange],
  );

  const handleOrientationChange = useCallback(
    (value: string) => onConfigChange('orientation', value as 'horizontal' | 'vertical'),
    [onConfigChange],
  );

  const handleVariantChange = useCallback(
    (value: string) => onConfigChange('variant', value as 'line' | 'pills' | 'bordered'),
    [onConfigChange],
  );

  const handleAnimateIndicatorChange = useCallback(
    (value: boolean) => onConfigChange('animateIndicator', value),
    [onConfigChange],
  );

  const handleScrollableTabsChange = useCallback(
    (value: boolean) => onConfigChange('scrollableTabs', value),
    [onConfigChange],
  );

  const orientationOptions = useMemo(
    () => [
      { label: 'Horizontal', value: 'horizontal' },
      { label: 'Vertical', value: 'vertical' },
    ],
    [],
  );

  const variantOptions = useMemo(
    () => [
      { label: 'Line', value: 'line' },
      { label: 'Pills', value: 'pills' },
      { label: 'Bordered', value: 'bordered' },
    ],
    [],
  );

  return (
    <div className="af-property-editor__section enhanced-tabs-editor">
      <h3 className="af-property-editor__section-title">Enhanced Tabs Properties</h3>

      <ColorPropertyEditor
        label="Active Tab Color"
        value={config.activeTabColor || '#309B48'}
        onChange={handleActiveTabColorChange}
        helpText="Text/indicator color for active tab"
      />

      <ColorPropertyEditor
        label="Inactive Tab Color"
        value={config.inactiveTabColor || '#6B7879'}
        onChange={handleInactiveTabColorChange}
        helpText="Text color for inactive tabs"
      />

      <ColorPropertyEditor
        label="Tab Background Color"
        value={config.tabBackgroundColor || '#FFFFFF'}
        onChange={handleTabBackgroundColorChange}
        helpText="Background color of tab header"
      />

      <ColorPropertyEditor
        label="Indicator Color"
        value={config.indicatorColor || '#309B48'}
        onChange={handleIndicatorColorChange}
        helpText="Underline/indicator color for active tab"
      />

      <NumberPropertyEditor
        label="Tab Height (px)"
        value={config.tabHeight || 48}
        onChange={handleTabHeightChange}
        min={32}
        max={80}
        step={4}
        helpText="Height of the tab header area"
      />

      <SelectPropertyEditor
        label="Orientation"
        value={config.orientation || 'horizontal'}
        onChange={handleOrientationChange}
        options={orientationOptions}
        helpText="Tab layout direction"
      />

      <SelectPropertyEditor
        label="Variant"
        value={config.variant || 'line'}
        onChange={handleVariantChange}
        options={variantOptions}
        helpText="Visual style of tabs"
      />

      <CheckboxPropertyEditor
        label="Animate Indicator"
        value={config.animateIndicator ?? true}
        onChange={handleAnimateIndicatorChange}
        helpText="Smooth animation when switching tabs"
      />

      <CheckboxPropertyEditor
        label="Scrollable Tabs"
        value={config.scrollableTabs || false}
        onChange={handleScrollableTabsChange}
        helpText="Enable horizontal scroll for many tabs"
      />
    </div>
  );
};

EnhancedTabsEditorComponent.displayName = 'EnhancedTabsEditor';

/**
 * Enhanced Tabs Property Editor Registration
 */
export const EnhancedTabsEditor: PropertyEditor = {
  type: 'enhanced-tabs',
  label: 'Enhanced Tabs',
  description: 'Configure tab appearance, colors, sizing, and behavior',
  version: '1.0.0',
  render: (props) => <EnhancedTabsEditorComponent {...props} />,
};

export default EnhancedTabsEditor;
